/**
 * Daily Usage Chart
 *
 * Displays a bar chart of daily request counts per canary
 * over the last 14 days. Uses CSS-only bars (no chart library).
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/services/api'
import { useCurrentOrgId } from '@/hooks/useCurrentOrgId'

function getDateRange(): { dateFrom: string; dateTo: string } {
  const now = new Date()
  const dateTo = now.toISOString().slice(0, 10)
  const from = new Date(now)
  from.setDate(from.getDate() - 13)
  const dateFrom = from.toISOString().slice(0, 10)
  return { dateFrom, dateTo }
}

type DayData = {
  date: string
  label: string
  totalRequests: number
}

export function UsageChart() {
  const { orgId } = useCurrentOrgId()
  const { dateFrom, dateTo } = useMemo(getDateRange, [])

  const { data, isLoading, error } = useQuery({
    queryKey: ['billing-usage', orgId, dateFrom, dateTo],
    queryFn: () => api.getUsage(orgId ?? '', { dateFrom, dateTo, limit: 100 }),
    enabled: !!orgId,
  })

  if (isLoading) {
    return (
      <div className="card px-4 py-5 sm:p-6">
        <div className="h-5 bg-gray-200 rounded w-40 mb-4 animate-pulse" />
        <div className="h-40 bg-gray-100 rounded animate-pulse" />
      </div>
    )
  }

  if (error) return null

  const days = buildDayData(dateFrom, data?.data ?? [])
  const maxRequests = Math.max(...days.map((d) => d.totalRequests), 1)

  return (
    <div className="card px-4 py-5 sm:p-6">
      <h3 className="text-sm font-medium text-gray-900 mb-4">
        Daily Requests (Last 14 Days)
      </h3>

      {days.every((d) => d.totalRequests === 0) ? (
        <p className="text-sm text-gray-500 text-center py-8">
          No request data available yet.
        </p>
      ) : (
        <div className="flex items-end gap-1 h-40">
          {days.map((day) => (
            <div
              key={day.date}
              className="flex-1 flex flex-col items-center justify-end h-full"
            >
              <div
                className="w-full bg-blue-500 rounded-t min-h-[2px] transition-all"
                style={{
                  height: `${(day.totalRequests / maxRequests) * 100}%`,
                }}
                title={`${day.date}: ${day.totalRequests.toLocaleString()} requests`}
              />
              <span className="text-[10px] text-gray-400 mt-1 leading-none">
                {day.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {!days.every((d) => d.totalRequests === 0) && (
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>0</span>
          <span>{maxRequests.toLocaleString()} req</span>
        </div>
      )}
    </div>
  )
}

function buildDayData(
  dateFrom: string,
  usageData: { usageDate: string; requestCount: number }[]
): DayData[] {
  const days: DayData[] = []
  const start = new Date(dateFrom)

  for (let i = 0; i < 14; i++) {
    const d = new Date(start)
    d.setDate(d.getDate() + i)
    const dateStr = d.toISOString().slice(0, 10)

    const total = usageData
      .filter((u) => u.usageDate === dateStr)
      .reduce((sum, u) => sum + u.requestCount, 0)

    days.push({
      date: dateStr,
      label: `${d.getMonth() + 1}/${d.getDate()}`,
      totalRequests: total,
    })
  }

  return days
}
